// 充值页面 - Stripe风格紧凑版设计
import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, Shield, CheckCircle, AlertCircle, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import LanguageSelector from '../components/LanguageSelector';

interface UserBalance {
  balance: number;
}

interface UserLevelInfo {
  current_level: number;
  level_name: string;
  reward_percentage: number;
  current_earnings: number;
  required_earnings: number;
  next_level_required: number | null;
  progress_to_next: number;
}

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  stripe_payment_intent_id?: string;
  completed_at?: string;
}

interface TransferRecord {
  id: string;
  amount: number;
  from_type: string;
  to_type: string;
  status: string;
  created_at: string;
  completed_at?: string;
  description?: string;
}

const Billing: React.FC = () => {
  const { t } = useTranslation();
  
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(10);
  const [customAmount, setCustomAmount] = useState('');
  const [error, setError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [userLevel, setUserLevel] = useState<UserLevelInfo | null>(null);
  // 支付记录相关状态
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalPayments, setModalPayments] = useState<PaymentRecord[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsModalLoading, setPaymentsModalLoading] = useState(false);// 支付状态跟踪相关状态
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [pollingTimer, setPollingTimer] = useState<NodeJS.Timeout | null>(null);
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'payment' | 'history' | 'transfer'>('payment');
  // Payment History相关状态
const [historyPage, setHistoryPage] = useState(1);
const [historyPageSize, setHistoryPageSize] = useState(20);
const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
const [historyTotalPages, setHistoryTotalPages] = useState(0);
const [historyLoading, setHistoryLoading] = useState(false);
const [historyPayments, setHistoryPayments] = useState<PaymentRecord[]>([]);

// Transfer History相关状态
const [transferPage, setTransferPage] = useState(1);
const [transferPageSize, setTransferPageSize] = useState(20);
const [transferTotalRecords, setTransferTotalRecords] = useState(0);
const [transferTotalPages, setTransferTotalPages] = useState(0);
const [transferLoading, setTransferLoading] = useState(false);
const [transferRecords, setTransferRecords] = useState<TransferRecord[]>([]);

  const quickAmounts = [
    { value: 10, label: '$10' },
    { value: 25, label: '$25' },
    { value: 50, label: '$50' },
    { value: 100, label: '$100' }
  ];

  // 启动支付状态轮询
  const startPaymentPolling = async () => {
    try {
      // 先获取当前真实余额，确保有正确的基准值
      const response = await api.get('/api/user/info');
      const realCurrentBalance = Number(response.data.balance);

      // 更新页面显示的余额
      setBalance(realCurrentBalance);

      // 使用真实余额作为基准
      const initialBalance = realCurrentBalance;

      // 使用局部变量管理定时器
      let currentTimer: NodeJS.Timeout | null = null;

      const pollPaymentStatus = async () => {
        try {
          // 检查超时（30秒）
          if (pollingStartTime && Date.now() - pollingStartTime > 30000) {
            if (currentTimer) {
              clearInterval(currentTimer);
              currentTimer = null;
            }
            setPaymentStatus('failed');
            setPollingStartTime(null);
            return;
          }

          // 获取最新余额（不更新页面显示，避免闪烁）
          const checkResponse = await api.get('/api/user/info');
          const currentBalance = Number(checkResponse.data.balance);

          // 检查余额是否有显著增加（>0.005美元）
          if (currentBalance > initialBalance + 0.005) {
            // 清理定时器
            if (currentTimer) {
              clearInterval(currentTimer);
              currentTimer = null;
            }

            // 更新页面余额和状态
            setBalance(currentBalance);
            setPaymentStatus('success');
            setPollingStartTime(null);
            fetchPaymentHistory();

            return;
          }

        } catch (err) {
          // 静默处理轮询错误，继续尝试
        }
      };

      // 设置定时轮询（不立即执行，给用户看到处理中状态）
      currentTimer = setInterval(pollPaymentStatus, 3000);

    } catch (error) {
      setPaymentStatus('failed');
    }
  };

  // 停止支付状态轮询
  const stopPaymentPolling = () => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      setPollingTimer(null);
    }
    setPollingStartTime(null);
  };

  useEffect(() => {
    fetchBalance();
    fetchUserLevel();
    fetchPaymentHistory();
  }, []);

  // 处理Stripe Checkout返回结果
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success) {
      // 设置支付处理中状态
      setPaymentStatus('processing');
      setPollingStartTime(Date.now());

      // 启动轮询检查支付状态
      startPaymentPolling();

      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled) {
      setError(t('billing.paymentCanceled'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // 组件卸载时清理轮询定时器
  useEffect(() => {
    return () => {
      stopPaymentPolling();
    };
  }, [pollingTimer]);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/api/user/info');
      setBalance(response.data.balance);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('billing.failedToLoadBalance'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLevel = async () => {
    try {
      const response = await api.get('/api/user/level');
      setUserLevel(response.data);
    } catch (err: any) {
      console.error('Failed to load user level:', err);
      // 如果获取等级信息失败，设置默认值
      setUserLevel({
        current_level: 1,
        level_name: t('billing.bronzeI'),
        reward_percentage: 10,
        current_earnings: 0,
        required_earnings: 100,
        next_level_required: 100,
        progress_to_next: 0
      });
    }
  };

  // 获取支付历史记录（最近几条用于显示）
  const fetchPaymentHistory = async () => {
    try {
      setPaymentsLoading(true);
      const response = await api.get('/api/user/payments?limit=5');
      setPaymentHistory(response.data.data || []);
    } catch (err: any) {
      // Error handled silently
    } finally {
      setPaymentsLoading(false);
    }
  };

  // 获取完整支付记录（用于弹窗）
  const fetchPaymentModal = async () => {
    try {
      setPaymentsModalLoading(true);
      const response = await api.get('/api/user/payments?limit=50');
      setModalPayments(response.data.data || []);
    } catch (err: any) {
      // Error handled silently
    } finally {
      setPaymentsModalLoading(false);
    }
  };

  // 获取分页支付历史记录
const fetchPaymentHistoryPaginated = async (page = 1, pageSize = 20) => {
  try {
    setHistoryLoading(true);
    const offset = (page - 1) * pageSize;
    const response = await api.get(`/api/user/payments?limit=${pageSize}&offset=${offset}`);
    
    setHistoryPayments(response.data.data || []);
    setHistoryTotalRecords(response.data.total || 0);
    setHistoryTotalPages(Math.ceil((response.data.total || 0) / pageSize));
    setHistoryPage(page);
  } catch (err: any) {
    // Error handled silently
  } finally {
    setHistoryLoading(false);
  }
};

const handleHistoryPageChange = (newPage: number) => {
  fetchPaymentHistoryPaginated(newPage, historyPageSize);
};

const handleHistoryPageSizeChange = (newPageSize: number) => {
  setHistoryPageSize(newPageSize);
  fetchPaymentHistoryPaginated(1, newPageSize);
};

// 获取分页余额转换记录
const fetchTransferHistoryPaginated = async (page = 1, pageSize = 20) => {
  try {
    setTransferLoading(true);
    const offset = (page - 1) * pageSize;
    const response = await api.get(`/api/balance/transfer-history?limit=${pageSize}&offset=${offset}`);
    
    setTransferRecords(response.data.data || []);
    setTransferTotalRecords(response.data.total || 0);
    setTransferTotalPages(Math.ceil((response.data.total || 0) / pageSize));
    setTransferPage(page);
  } catch (err: any) {
    // Error handled silently
  } finally {
    setTransferLoading(false);
  }
};

const handleTransferPageChange = (newPage: number) => {
  fetchTransferHistoryPaginated(newPage, transferPageSize);
};

const handleTransferPageSizeChange = (newPageSize: number) => {
  setTransferPageSize(newPageSize);
  fetchTransferHistoryPaginated(1, newPageSize);
};

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError('');
  };

  const handleCustomAmount = (value: string) => {
    const numValue = value.replace(/[^0-9.]/g, '');
    setCustomAmount(numValue);

    const amount = parseFloat(numValue);
    if (!isNaN(amount) && amount > 0) {
      setSelectedAmount(amount);
    } else {
      setSelectedAmount(null);
    }
    setError('');
  };

  const redirectToCheckout = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      setError(t('pleaseSelectAmount'));
      return;
    }

    if (selectedAmount < 1) {
      setError(t('minimumAmount'));
      return;
    }

    try {
      setError('');
      setPaymentLoading(true);

      const response = await api.post('/api/payments/create-checkout', {
        amount: selectedAmount,
        currency: 'usd'
      });

      // 直接跳转到Stripe Checkout页面
      window.location.href = response.data.data.checkout_url;

    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('failedToCreateCheckout'));
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <LoadingSpinner size="medium" text={t('billing.loadingBillingInfo')} fullPage={true} />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* 左侧导航 - 固定定位 */}
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 ml-[-448px] w-72 z-10">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('payment')}
                  className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors ${
                    activeTab === 'payment'
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('billing.addFunds')}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('history');
                    if (historyPayments.length === 0) {
                      fetchPaymentHistoryPaginated();
                    }
                  }}
                  className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors ${
                    activeTab === 'history'
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('billing.paymentHistory')}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('transfer');
                    if (transferRecords.length === 0) {
                      fetchTransferHistoryPaginated();
                    }
                  }}
                  className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors ${
                    activeTab === 'transfer'
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('billing.rewardsToBalanceHistory')}
                </button>
              </nav>
            </div>
          </div>



          <div className="grid grid-cols-12 gap-8">
            {/* 左侧占位空间 */}
            <div className="col-span-3"></div>


            {/* 右侧主内容区域 */}
            <div className="col-span-9">
              {activeTab === 'payment' ? (
                // 支付选项卡内容
                <div className="space-y-6">
                  {/* 错误或成功消息 */}
                  {error && (
                    <div className={`p-3 rounded-md border text-sm max-w-4xl ${
                      error.includes('canceled') || error.includes('Failed') || error.includes('select')
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-green-50 border-green-200 text-green-700'
                    }`}>
                      <div className="flex items-center">
                        {error.includes('canceled') || error.includes('Failed') || error.includes('select') ?
                          <AlertCircle className="h-4 w-4 mr-2" /> :
                          <CheckCircle className="h-4 w-4 mr-2" />
                        }
                        {error}
                      </div>
                    </div>
                  )}

                  {/* 主要内容区域 - 左右布局 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
                    {/* 左侧：余额和充值区域 */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      {/* 当前余额 */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900">{t('billing.currentBalance')}</h3>
                            <p className="text-2xl font-bold text-gray-900">${balance.toFixed(2)}</p>

                            {/* 简洁的支付状态提示 */}
                            {paymentStatus === 'processing' && (
                              <div className="flex items-center mt-3">
                                <LoadingSpinner size="small" text="" className="mr-2" />
                                <span className="text-sm text-gray-700 font-medium">
                                  {t('billing.processingPayment')}
                                  <span className="inline-block">
                                    <span className="animate-pulse">.</span>
                                    <span className="animate-pulse" style={{ animationDelay: '0.5s' }}>.</span>
                                    <span className="animate-pulse" style={{ animationDelay: '1s' }}>.</span>
                                  </span>
                                </span>
                              </div>
                            )}

                            {paymentStatus === 'success' && (
                              <div className="flex items-center mt-3">
                                <CheckCircle className="w-4 h-4 text-gray-600 mr-2" />
                                <span className="text-sm text-gray-700 font-medium">{t('billing.paymentSuccessful')}</span>
                              </div>
                            )}

                            {paymentStatus === 'failed' && (
                              <div className="flex items-center mt-3">
                                <AlertCircle className="w-4 h-4 text-gray-600 mr-2" />
                                <span className="text-sm text-gray-700 font-medium">{t('billing.paymentProcessingTimeout')}</span>
                              </div>
                            )}
                          </div>
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="h-6 w-6 text-gray-600" />
                          </div>
                        </div>
                      </div>

                      {/* 分隔线 */}
                      <div className="border-t border-gray-200 mb-6"></div>

                      {/* 充值选项 */}
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('billing.addFunds')}</h3>

                    {/* 快速金额选择 */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {quickAmounts.map((amount) => (
                          <button
                            key={amount.value}
                            onClick={() => handleAmountSelect(amount.value)}
                            className={`relative px-3 py-2 text-xs font-medium rounded border transition-all ${
                              selectedAmount === amount.value
                                ? 'border-gray-500 bg-gray-100 text-gray-900'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {amount.label}
                          </button>
                        ))}
                    </div>

                    {/* 自定义金额 */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        {t('billing.orEnterCustomAmount')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">$</span>
                        </div>
                        <input
                          type="text"
                          value={customAmount}
                          onChange={(e) => handleCustomAmount(e.target.value)}
                          placeholder="0.00"
                          className="block w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">{t('billing.minimum')}</p>
                    </div>

                    {/* 支付按钮 */}
                    <button
                        onClick={redirectToCheckout}
                        disabled={!selectedAmount || selectedAmount <= 0 || paymentLoading}
                        className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                          !selectedAmount || selectedAmount <= 0 || paymentLoading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {paymentLoading ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="small" text="" className="mr-1" />
                            <span className="text-xs">{t('billing.redirecting')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <CreditCard className="h-4 w-4 mr-2" />
                            Add ${selectedAmount ? selectedAmount.toFixed(2) : '0.00'}
                          </div>
                        )}
                      </button>
                    </div>

                    {/* 右侧：用户等级和分成奖励卡片 */}
                     <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 h-full">
                       <div className="flex flex-col h-full">
                         <div className="flex items-center mb-4">
                           <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                             <DollarSign className="h-5 w-5 text-gray-600" />
                           </div>
                           <h3 className="text-lg font-medium text-gray-900">{t('billing.yourLevelRewards')}</h3>
                         </div>
                         
                         <div className="flex-1 flex flex-col justify-center">
                           {userLevel ? (
                             <>
                               <div className="text-center mb-4">
                                 <div className="inline-flex items-center bg-slate-700 text-white px-4 py-2 rounded-lg mb-2">
                                   <Crown className="h-5 w-5 text-yellow-500 mr-3" />
                                   <span className="text-lg font-semibold">{userLevel.level_name}</span>
                                 </div>
                                 <p className="text-sm text-gray-600 mb-3">{t('billing.currentLevel')}</p>
                               </div>
                               
                               <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600 mb-1">{userLevel.reward_percentage}%</div>
                                    <div className="text-sm font-medium text-gray-800 mb-1">{t('billing.commissionRate')}</div>
                                    <div className="text-xs text-gray-600 mb-3">{t('billing.earnFromReferrals')}</div>
                                    
                                    <div className="border-t border-gray-200 pt-3 mt-3">
                                      <div className="text-lg font-bold text-blue-600 mb-1">${userLevel.current_earnings.toFixed(2)}</div>
                                      <div className="text-xs text-gray-600">{t('billing.totalEarnings')}</div>
                                    </div>
                                  </div>
                                </div>
                             </>
                           ) : (
                             <div className="text-center">
                               <div className="text-gray-500">{t('billing.loadingLevelInfo')}</div>
                             </div>
                           )}
                         </div>
                       </div>
                     </div>
                  </div>

                  {/* 企业方案区域 */}
                   <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 max-w-4xl">
                     <div className="flex items-start">
                       <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                         <Shield className="h-6 w-6 text-gray-600" />
                       </div>
                       <div className="flex-1">
                         <h3 className="text-xl font-bold text-gray-900 mb-2">{t('billing.enterpriseSolutions')}</h3>
                         <p className="text-gray-600 mb-4">
                           {t('billing.enterpriseDescription')}
                         </p>
                         
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                           <div className="flex items-center">
                             <CheckCircle className="h-4 w-4 text-gray-600 mr-2" />
                             <span className="text-sm text-gray-700">{t('billing.volumeDiscounts')}</span>
                           </div>
                           <div className="flex items-center">
                             <CheckCircle className="h-4 w-4 text-gray-600 mr-2" />
                             <span className="text-sm text-gray-700">{t('billing.dedicatedSupport')}</span>
                           </div>
                           <div className="flex items-center">
                             <CheckCircle className="h-4 w-4 text-gray-600 mr-2" />
                             <span className="text-sm text-gray-700">{t('billing.flexiblePayment')}</span>
                           </div>
                         </div>
                         
                         <div>
                           <p className="text-sm text-gray-600">{t('billing.contactForPricing')}</p>
                           <a href="mailto:service@modelpool.net" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                             service@modelpool.net
                           </a>
                         </div>
                       </div>
                     </div>
                   </div>
                </div>
              ) : activeTab === 'history' ? (
                // 支付历史选项卡内容
                <div className="bg-white rounded-lg border border-gray-200">
                  {/* 表格头部 */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-medium text-gray-900">{t('billing.paymentHistoryTitle')}</h3>
                  </div>
                  
                  {/* 表格内容 */}
                  <div className="overflow-hidden">
                    {historyLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner size="small" text={t('billing.loadingPaymentHistory')} />
                      </div>
                    ) : historyPayments.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.amount')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.status')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.created')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.completed')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.paymentId')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {historyPayments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  ${payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    payment.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : payment.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {payment.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(payment.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {payment.completed_at 
                                    ? new Date(payment.completed_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    : '-'
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                  {payment.stripe_payment_intent_id 
                                    ? payment.stripe_payment_intent_id.slice(-12)
                                    : payment.id.slice(0, 12) + '...'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <CreditCard className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-600">{t('billing.noPaymentHistory')}</p>
                        <p className="text-xs text-gray-500">{t('billing.paymentRecordsWillAppear')}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* 紧凑分页控件 */}
                  {historyTotalRecords > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-600">
                          {Math.min((historyPage - 1) * historyPageSize + 1, historyTotalRecords)} - {Math.min(historyPage * historyPageSize, historyTotalRecords)} {t('billing.of')} {historyTotalRecords}
                        </span>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-600">{t('billing.show')}:</span>
                          <select
                            value={historyPageSize}
                            onChange={(e) => handleHistoryPageSizeChange(Number(e.target.value))}
                            className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const pages = [];
                          const maxButtons = 5;
                          let startPage = Math.max(1, historyPage - 2);
                          let endPage = Math.min(historyTotalPages, startPage + maxButtons - 1);
                          
                          if (endPage - startPage + 1 < maxButtons) {
                            startPage = Math.max(1, endPage - maxButtons + 1);
                          }
                          
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => handleHistoryPageChange(1)}
                                className="w-6 h-6 text-xs border border-gray-300 rounded hover:bg-gray-100 flex items-center justify-center"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(<span key="ellipsis1" className="text-xs text-gray-400 px-1">...</span>);
                            }
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handleHistoryPageChange(i)}
                                className={`w-6 h-6 text-xs rounded flex items-center justify-center ${
                                  i === historyPage
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          if (endPage < historyTotalPages) {
                            if (endPage < historyTotalPages - 1) {
                              pages.push(<span key="ellipsis2" className="text-xs text-gray-400 px-1">...</span>);
                            }
                            pages.push(
                              <button
                                key={historyTotalPages}
                                onClick={() => handleHistoryPageChange(historyTotalPages)}
                                className="w-6 h-6 text-xs border border-gray-300 rounded hover:bg-gray-100 flex items-center justify-center"
                              >
                                {historyTotalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Rewards to Balance History选项卡内容
                <div className="bg-white rounded-lg border border-gray-200">
                  {/* 表格头部 */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-medium text-gray-900">{t('billing.rewardsToBalanceHistoryTitle')}</h3>
                  </div>
                  
                  {/* 表格内容 */}
                  <div className="overflow-hidden">
                    {transferLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner size="small" text={t('billing.loadingTransferHistory')} />
                      </div>
                    ) : transferRecords.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.amount')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.status')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.created')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing.description')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {transferRecords.map((record) => (
                              <tr key={record.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  ${record.amount.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    record.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : record.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {record.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(record.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {record.description || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <DollarSign className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-600">{t('billing.noTransferHistory')}</p>
                        <p className="text-xs text-gray-500">{t('billing.transferRecordsWillAppear')}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* 紧凑分页控件 */}
                  {transferTotalRecords > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-600">
                          {Math.min((transferPage - 1) * transferPageSize + 1, transferTotalRecords)} - {Math.min(transferPage * transferPageSize, transferTotalRecords)} {t('of')} {transferTotalRecords}
                        </span>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-600">{t('show')}:</span>
                          <select
                            value={transferPageSize}
                            onChange={(e) => handleTransferPageSizeChange(Number(e.target.value))}
                            className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const pages = [];
                          const maxButtons = 5;
                          let startPage = Math.max(1, transferPage - 2);
                          let endPage = Math.min(transferTotalPages, startPage + maxButtons - 1);
                          
                          if (endPage - startPage + 1 < maxButtons) {
                            startPage = Math.max(1, endPage - maxButtons + 1);
                          }
                          
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => handleTransferPageChange(1)}
                                className="w-6 h-6 text-xs border border-gray-300 rounded hover:bg-gray-100 flex items-center justify-center"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(<span key="ellipsis1" className="text-xs text-gray-400 px-1">...</span>);
                            }
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handleTransferPageChange(i)}
                                className={`w-6 h-6 text-xs rounded flex items-center justify-center ${
                                  i === transferPage
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          if (endPage < transferTotalPages) {
                            if (endPage < transferTotalPages - 1) {
                              pages.push(<span key="ellipsis2" className="text-xs text-gray-400 px-1">...</span>);
                            }
                            pages.push(
                              <button
                                key={transferTotalPages}
                                onClick={() => handleTransferPageChange(transferTotalPages)}
                                className="w-6 h-6 text-xs border border-gray-300 rounded hover:bg-gray-100 flex items-center justify-center"
                              >
                                {transferTotalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;